import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import type { ElasticVehiclesResponse } from '@//app/api/search/vehicles';
import { useApiBaseUrl } from '@//providers/MarketSettingsProvider';
import { httpClientGetJson } from '../../httpClient';
import { createSearchParams, type OverrideParams } from '../../utils';

export const overrideVehicleSearchParams: OverrideParams = {
  hits: 24,
};

interface UseVehiclesSettings {
  initialData?: ElasticVehiclesResponse;
}

const useVehicles = (params: URLSearchParams, settings?: UseVehiclesSettings) => {
  const apiBaseUrl = useApiBaseUrl();
  const { queryString } = createSearchParams(params, overrideVehicleSearchParams);

  const [pollInterval, setPollInterval] = useState<number | false>(false);

  useEffect(() => {
    window.pollSearch = (enabled = true) => setPollInterval(enabled ? 60000 : false);

    return () => {
      delete window.pollSearch;
    };
  }, []);

  // SSR initialData is only valid for the query that the server actually ran. Client-side
  // filter changes update the URL via pushState (no Server Component re-execution), so the
  // initialData prop stays frozen at its first-mount value. Capture the queryString of that
  // first mount and only apply initialData when the current key still matches.
  const [initialQueryString] = useState(queryString);
  const initialData = initialQueryString === queryString ? settings?.initialData : undefined;
  // staleTime must exceed Cloudflare's max-age (60s) + stale-while-revalidate (120s),
  // otherwise dehydrated query state baked into cached HTML is considered stale on
  // hydration and triggers a refetch that races with React's hydration pass.
  const query = useQuery<ElasticVehiclesResponse>({
    queryKey: ['vehicles', queryString],
    queryFn: () =>
      httpClientGetJson<ElasticVehiclesResponse>(`${apiBaseUrl}/api/search/vehicles${queryString}`),
    initialData,
    initialDataUpdatedAt: initialData ? Date.now() : undefined,
    staleTime: initialData ? 300_000 : 0,
    refetchOnMount: !initialData,
    placeholderData: (previous) => previous,
    refetchInterval: pollInterval,
  });

  const isPolling = pollInterval !== false;

  return { ...query, isPolling };
};

export default useVehicles;
